<script lang="ts" context="module">
  import type { ComponentType } from 'svelte';

  export type ComboboxOption = {
    id: number | string;
    label: string;
    description?: string;
    itemCount?: number;
    icon?: ComponentType | null;
  };
</script>

<script lang="ts">
  import ErrorWarningAlertIcon from '$icons/ErrorWarningAlertIcon.svelte';
  import { cn } from '$lib/utils/misc';
  import { clickOutside } from '$utils';
  import XCircleIcon from '$icons/XCircleIcon.svelte';

  export let id: string | undefined = undefined;
  export let value: string = '';
  export let searchValue: string = '';
  export let options: ComboboxOption[] = [];
  export let label = '';
  export let description = '';
  export let name = '';
  export let placeholder = 'Select';
  export let errors: string[] = [];
  export let optional = false;
  export let onChange: (option: ComboboxOption | null) => void = () => {};

  let isOpen = false;
  let filteredOptions = options;
  let selectedIndex = -1;
  let listboxElement: HTMLUListElement;
  let isMouseDownOnOption = false;

  // Handle external value changes
  $: if (value) {
    const selectedOption = options.find((opt) => String(opt.id) === value);
    if (selectedOption && !searchValue) {
      searchValue = selectedOption.label;
    }
  }

  // Handle search filtering
  $: {
    if (!value) {
      // Only filter if no value is selected
      if (searchValue) {
        filteredOptions = options.filter((option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase()),
        );
      } else {
        filteredOptions = options;
      }
    }
  }

  function onOptionSelected(option: ComboboxOption) {
    value = String(option.id);
    searchValue = option.label;
    isOpen = false;
    onChange(option);
  }

  function clearSelection() {
    value = '';
    searchValue = '';
    filteredOptions = options; // Reset filtered options
    onChange(null);
  }

  function handleKeydown(event: KeyboardEvent) {
    // Clear selection if backspace is pressed and a value is selected
    if (
      event.key === 'Backspace' &&
      value &&
      searchValue === options.find((opt) => String(opt.id) === value)?.label
    ) {
      event.preventDefault();
      clearSelection();
      isOpen = true;
      return;
    }

    // Open the dropdown if the user presses arrow down or enter
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        isOpen = true;
      }
      return;
    }

    // Handle arrow keys and enter key
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, filteredOptions.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && filteredOptions[selectedIndex]) {
          onOptionSelected(filteredOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        isOpen = false;
        break;
    }
  }

  $: if (selectedIndex >= 0 && listboxElement) {
    setTimeout(() => {
      const buttons = listboxElement.querySelectorAll('button[role="option"]');
      const selectedButton = buttons[selectedIndex];
      if (selectedButton) {
        selectedButton.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }

  $: {
    if (isOpen && filteredOptions.length > 0) {
      selectedIndex = 0;
    } else if (!isOpen || filteredOptions.length === 0) {
      selectedIndex = -1;
    }
  }
</script>

<div class="font-inter flex w-full flex-col items-start text-xs font-normal leading-4">
  <label for={id}>
    {#if label}
      <p class="font-architekt text-gray-dark-12 mb-1 text-sm font-normal leading-5">
        {label}
        {#if optional}
          <span class="text-gray-dark-10 text-[12px]">(Optional)</span>
        {/if}
      </p>
    {/if}
    {#if description}
      <p class="text-gray-dark-11 mb-2 mt-1 text-xs">
        {@html description}
      </p>
    {/if}
  </label>

  <div class="relative h-10 w-full" use:clickOutside={() => (isOpen = false)}>
    <input
      {id}
      {name}
      type="text"
      {placeholder}
      autocomplete="off"
      bind:value={searchValue}
      on:focus={() => (isOpen = true)}
      on:blur={() => {
        if (!isMouseDownOnOption) {
          isOpen = false;
        }
      }}
      on:keydown={handleKeydown}
      class="border-gray-dark-4 text-gray-dark-12 caret-mint-dark-11 hover:border-gray-dark-5 focus:border-mint-dark-8 focus:ring-mint-dark-4 placeholder:text-gray-dark-8 h-full w-full rounded border bg-transparent pl-4 pr-10 outline-none ring-[3px] ring-transparent"
      class:pl-8={value && options.find((opt) => String(opt.id) === value)?.icon}
      role="combobox"
      aria-controls="options"
      aria-expanded={isOpen}
    />
    {#if value && options.find((opt) => String(opt.id) === value)?.icon}
      <div class="absolute inset-y-0 left-0 flex items-center pl-3">
        <svelte:component this={options.find((opt) => String(opt.id) === value)?.icon} />
      </div>
    {/if}
    <button
      type="button"
      tabindex="-1"
      class="text-gray-dark-9 absolute inset-y-0 right-0 flex items-center px-3"
      on:click={() => {
        if (value) {
          clearSelection();
        } else {
          isOpen = !isOpen;
        }
      }}
    >
      {#if value && value.length > 0}
        <XCircleIcon class="hover:text-gray-dark-12 size-4" />
      {:else}
        <svg
          class={cn('size-5 transform duration-500', isOpen ? 'scale-105' : 'scale-100')}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fill-rule="evenodd"
            d="M10.53 3.47a.75.75 0 0 0-1.06 0L6.22 6.72a.75.75 0 0 0 1.06 1.06L10 5.06l2.72 2.72a.75.75 0 1 0 1.06-1.06l-3.25-3.25Zm-4.31 9.81 3.25 3.25a.75.75 0 0 0 1.06 0l3.25-3.25a.75.75 0 1 0-1.06-1.06L10 14.94l-2.72-2.72a.75.75 0 0 0-1.06 1.06Z"
            clip-rule="evenodd"
          />
        </svg>
      {/if}
    </button>

    {#if isOpen}
      <div
        class="bg-gray-dark-3 py-2xs absolute inset-x-0 z-10 h-fit max-h-[188px] overflow-y-scroll rounded"
      >
        <ul role="listbox" bind:this={listboxElement}>
          {#if filteredOptions.length > 0}
            {#each filteredOptions as option, i}
              <button
                type="button"
                tabindex="-1"
                data-index={i}
                on:mousedown={() => (isMouseDownOnOption = true)}
                on:mouseup={() => (isMouseDownOnOption = false)}
                on:click={() => onOptionSelected(option)}
                class={cn(
                  'px-lg text-gray-dark-12 hover:bg-gray-dark-2 focus:border-mint-dark-8 focus-within:ring-mint-dark-4 flex w-full flex-col items-start gap-y-1 border border-transparent py-2 text-sm leading-5 ring-[3px] ring-transparent',
                  selectedIndex === i ? 'bg-gray-dark-2' : '',
                )}
                role="option"
                aria-selected={value === option.id || selectedIndex === i}
              >
                <div class="flex items-center gap-2">
                  {#if option.icon}
                    <svelte:component this={option.icon} />
                  {/if}
                  <span class="truncate">{option.label}</span>
                  {#if option.itemCount}
                    <span class="text-gray-dark-10 text-xs">× {option.itemCount}</span>
                  {/if}
                </div>
                {#if option.description}
                  <span class="text-gray-dark-8 line-clamp-2 text-left text-xs"
                    >{option.description}</span
                  >
                {/if}
              </button>
            {/each}
          {:else}
            <div class="text-gray-dark-11 px-lg py-2 text-sm">No results found</div>
          {/if}
        </ul>
      </div>
    {/if}
  </div>

  {#if errors?.length > 0}
    {#each errors as error}
      {#if error}
        <div class="text-red-dark-10 mt-2 flex items-start justify-start gap-x-1 gap-y-0.5">
          <ErrorWarningAlertIcon class="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p class="text-red-dark-10">{error}</p>
        </div>
      {/if}
    {/each}
  {/if}
</div>

<style>
  ::-webkit-scrollbar-thumb {
    background: #006d5b;
  }
</style>
